import {
  Color,
  DoubleSide,
  KeepStencilOp,
  NotEqualStencilFunc,
  ReplaceStencilOp,
  ShaderMaterial,
  Vector2,
} from "three";

/** Name of the object containing the guided navigation guides, used to dispatch them to the cosmetics pass */
export const GUIDED_NAVIGATION_GUIDES_NAME = "GuidedNavigationGuides";

/** Color of the guided navigation guides */
const GUIDES_COLOR = new Color("#1f65f0");

/** Uniforms of the corridor floor material */
export type CorridorFloorUniforms = {
  uColor: { value: Color };
  uOpacity: { value: number };
};

/** Uniforms of the grid wall material */
export type GridWallUniforms = {
  /** Wall size in meters */
  uSize: { value: Vector2 };
  /** Grid cell size in meters */
  uCellSize: { value: number };
  uColor: { value: Color };
  uOpacity: { value: number };
};

/**
 * @returns the material rendering a corridor floor mesh with a uniform color. The stencil test makes each pixel
 * be drawn once even where the mesh triangles overlap, so the stencil buffer must be cleared before drawing it.
 */
export function createCorridorFloorMaterial(): ShaderMaterial & {
  uniforms: CorridorFloorUniforms;
} {
  const uniforms: CorridorFloorUniforms = {
    uColor: { value: GUIDES_COLOR.clone() },
    uOpacity: { value: 0 },
  };
  return Object.assign(
    new ShaderMaterial({
      uniforms,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      side: DoubleSide,
      stencilWrite: true,
      stencilRef: 1,
      stencilFunc: NotEqualStencilFunc,
      stencilFail: KeepStencilOp,
      stencilZFail: KeepStencilOp,
      stencilZPass: ReplaceStencilOp,
      vertexShader: /* glsl */ `
        void main() {
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        uniform vec3 uColor;
        uniform float uOpacity;

        void main() {
          if (uOpacity < 0.002) discard;
          gl_FragColor = vec4(uColor, uOpacity);
        }`,
    }),
    { uniforms },
  );
}

/**
 * @returns the material rendering a local grid with a radial fade, expecting a PlaneGeometry
 * @param width wall width in meters
 * @param height wall height in meters
 */
export function createGridWallMaterial(
  width: number,
  height: number,
): ShaderMaterial & { uniforms: GridWallUniforms } {
  const uniforms: GridWallUniforms = {
    uSize: { value: new Vector2(width, height) },
    uCellSize: { value: 0.2 },
    uColor: { value: GUIDES_COLOR.clone() },
    uOpacity: { value: 0 },
  };
  return Object.assign(
    new ShaderMaterial({
      uniforms,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      side: DoubleSide,
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        uniform vec2 uSize;
        uniform float uCellSize;
        uniform vec3 uColor;
        uniform float uOpacity;
        varying vec2 vUv;

        void main() {
          vec2 coord = vUv * uSize / uCellSize;
          vec2 grid = abs(fract(coord - 0.5) - 0.5) / fwidth(coord);
          float line = 1.0 - min(min(grid.x, grid.y), 1.0);
          float falloff = 1.0 - smoothstep(0.3, 1.0, length((vUv - 0.5) * 2.0));
          float alpha = (0.85 * line + 0.1) * falloff * uOpacity;
          if (alpha < 0.002) discard;
          gl_FragColor = vec4(uColor, alpha);
        }`,
    }),
    { uniforms },
  );
}
